import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import PageContainer from "../../components/page-container";
import s from "./styles.module.scss";
import { ReactComponent as InfoIcon } from "../../assets/info.svg";
import { ReactComponent as ClearLogo } from "../../assets/clearlogo.svg";
import { useTypewriter } from "../../hooks/use-type-writer";
import { useJobPolling } from "../../hooks/use-job-polling";


export const StreamingLoading = ({ text, description }) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { getToken } = useAuth();
  const hasNavigatedRef = useRef(false);
  console.log('[STREAMING-LOADING] First render, state:', state);
  console.log('[STREAMING-LOADING] initialMessage:', state?.initialMessage);

  const { jobId, calendar, initialMessage, isPatchJob } = state || {};
  const [activeJobId, setActiveJobId] = useState(jobId);

  // Poll job status - but stop polling once we navigate to insertion phase
  const { status, phase, message, totalEvents } = useJobPolling(
    activeJobId, // Use state so we can stop polling by setting to null
    getToken,

    // On completion
    (batch_id, total_events) => {
      if (!hasNavigatedRef.current) {
        hasNavigatedRef.current = true;
        setActiveJobId(null); // Stop polling

        if (isPatchJob) {
          // Patch job - return to courses screen with success modal
          console.log('[STREAMING-LOADING] Patch job completed, navigating to courses');
          navigate('/courses', {
            replace: true,
            state: { showSuccessModal: true }
          });
        } else {
          // Insert job - go to loading screen for insertion phase
          console.log('[STREAMING-LOADING] Job completed, navigating to loading');
          navigate('/loading', { state: { jobId, calendar, totalEvents: total_events }, replace: true });
        }
      }
    },

    // On error
    (error) => {
      console.error('Job failed:', error);
      if (!hasNavigatedRef.current) {
        hasNavigatedRef.current = true;
        setActiveJobId(null); // Stop polling

        if (isPatchJob) {
          // Patch job error - return to courses screen with error modal
          navigate('/courses', {
            replace: true,
            state: {
              showErrorModal: true,
              errorMessage: error || "Failed to save changes"
            }
          });
        } else {
          navigate('/no-events', {
            replace: true,
            state: {
              courseName: "Course Processing",
              errorMessage: error || "Job failed during processing",
            },
          });
        }
      }
    }
  );

  // Use typewriter effect for progress messages
  const { displayedText } = useTypewriter(message || initialMessage || "", 30);

  // When phase changes to 'insertion', switch to insertion loading screen (insert jobs only)
  // Patch jobs stay on this screen for the entire flow
  useEffect(() => {
    if (isPatchJob) return;

    if (phase === 'insertion' && !hasNavigatedRef.current) {
      console.log('[STREAMING-LOADING] Phase changed to insertion, waiting 500ms before navigating...');
      hasNavigatedRef.current = true;
      setActiveJobId(null); // Stop polling IMMEDIATELY

      // Wait 500ms to let the database transaction commit
      setTimeout(() => {
        console.log('[STREAMING-LOADING] Navigating to loading screen now');
        navigate('/loading', {
          replace: true,
          state: { jobId, calendar, totalEvents }
        });
      }, 500);
    }
  }, [phase, navigate, jobId, calendar, totalEvents, isPatchJob]);

  return (
    <PageContainer>
      <h1 className={s.title}>Loading events</h1>
      <p className={s.description}>
        Event extraction takes ~30s per syllabus (up to 1 min for longer files)
      </p>

      <div className={s.infoMessage}>
        <InfoIcon className={s.infoIcon} />
        <span>Please do not refresh this page</span>
      </div>

      <div className={s.logoAndMessage}>
        <ClearLogo className={`${s.logo} ${!displayedText || status === 'pending' || status === 'running' ? s.levitating : ''}`} />
        {displayedText && (
          <span className={s.streamingText}>{displayedText}</span>
        )}
      </div>
    </PageContainer>
  );
};
